import React, { useState, useEffect, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import axios from 'axios';
import { trackTradeImportStarted, trackTradeImportCompleted, trackTradeImportFailed } from '../../services/analytics';
import { Upload, X, ChevronRight, ChevronLeft, Check, AlertCircle, SkipForward } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import API_URL from '../../api/config';

// ─── Field definitions ────────────────────────────────────────────────────────

const REQUIRED_FIELDS = [
  { key: 'symbol',      label: 'Symbol',      description: 'Instrument / pair',     keywords: ['symbol', 'pair', 'instrument', 'asset', 'ticker'] },
  { key: 'direction',   label: 'Direction',   description: 'Buy or Sell',            keywords: ['type', 'direction', 'side', 'action', 'order'] },
  { key: 'openTime',    label: 'Open Time',   description: 'Entry timestamp',        keywords: ['open time', 'opentime', 'entry time', 'entrytime', 'time', 'open_time', 'entry_time'] },
  { key: 'entry_price', label: 'Open Price',  description: 'Entry price',            keywords: ['open price', 'openprice', 'entry price', 'entryprice', 'open', 'price', 'open_price', 'entry_price'] },
  { key: 'closeTime',   label: 'Close Time',  description: 'Exit timestamp',         keywords: ['close time', 'closetime', 'exit time', 'exittime', 'close_time', 'exit_time'] },
  { key: 'exit_price',  label: 'Close Price', description: 'Exit price',             keywords: ['close price', 'closeprice', 'exit price', 'exitprice', 'close', 'close_price', 'exit_price'] },
  { key: 'lot_size',    label: 'Volume',      description: 'Lot size / quantity',    keywords: ['volume', 'lots', 'lot size', 'lotsize', 'quantity', 'size', 'lot_size', 'position size'] },
  { key: 'profit',      label: 'Profit',      description: 'P&L in account currency',keywords: ['profit', 'pnl', 'p&l', 'net profit', 'gain', 'return', 'net_profit'] },
];

const OPTIONAL_FIELDS = [
  { key: 'pips',       label: 'Pips',       keywords: ['pips', 'points', 'pip'] },
  { key: 'currency',   label: 'Currency',   keywords: ['currency', 'account currency', 'ccy'] },
  { key: 'commission', label: 'Commission', keywords: ['commission', 'fee', 'fees', 'brokerage', 'comm'] },
  { key: 'swap',       label: 'Swap',       keywords: ['swap', 'overnight', 'financing', 'rollover', 'interest'] },
];

const ALL_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];
const REQUIRED_KEYS = new Set(REQUIRED_FIELDS.map(f => f.key));

// ─── Auto-mapping ─────────────────────────────────────────────────────────────

function normalizeHeader(h) {
  return String(h).toLowerCase().replace(/[_\s-]+/g, ' ').trim();
}

function autoMapColumns(headers) {
  const mapping = {};
  const normalized = headers.map(normalizeHeader);

  for (const field of ALL_FIELDS) {
    let bestHeader = '';
    let bestScore = -1;

    for (let i = 0; i < headers.length; i++) {
      const h = normalized[i];
      for (const kw of field.keywords) {
        let score = 0;
        if (h === kw) score = 100;
        else if (h.includes(kw) || kw.includes(h)) score = 50;
        if (score > bestScore) { bestScore = score; bestHeader = headers[i]; }
      }
    }

    mapping[field.key] = bestScore > 0 ? bestHeader : '';
  }

  return mapping;
}

// ─── Direction normalizer ─────────────────────────────────────────────────────

function normalizeDirection(raw) {
  if (!raw) return null;
  const v = String(raw).toLowerCase().trim();
  if (['buy', 'b', 'long', '1', 'in'].includes(v)) return 'BUY';
  if (['sell', 's', 'short', '-1', '0', 'out'].includes(v)) return 'SELL';
  return String(raw).toUpperCase();
}

// ─── Date parser ──────────────────────────────────────────────────────────────

function parseDate(raw) {
  if (!raw) return null;
  // MT5 format: 2023.01.15 10:30:00
  const mt5 = String(raw).replace(/\./g, '-').replace(' ', 'T');
  const d = new Date(mt5);
  if (!isNaN(d.getTime())) return d.toISOString();
  return null;
}

// ─── Transform raw CSV row → normalized trade object ─────────────────────────

function applyMapping(row, mapping) {
  const get = (key) => {
    const col = mapping[key];
    return col ? row[col] : undefined;
  };

  return {
    symbol:      String(get('symbol') || '').toUpperCase().trim(),
    direction:   normalizeDirection(get('direction')),
    openTime:    parseDate(get('openTime')),
    entry_price: parseFloat(get('entry_price')),
    closeTime:   parseDate(get('closeTime')),
    exit_price:  get('exit_price') != null ? parseFloat(get('exit_price')) : null,
    lot_size:    parseFloat(get('lot_size')) || 0,
    profit:      parseFloat(get('profit')) || 0,
    pips:        get('pips') != null ? parseFloat(get('pips')) : 0,
    currency:    get('currency') || 'USD',
    commission:  get('commission') != null ? parseFloat(get('commission')) : 0,
    swap:        get('swap') != null ? parseFloat(get('swap')) : 0,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ImportTradesModal({ open, onClose, onImportComplete }) {
  const [step, setStep] = useState(1); // 1=upload, 2=map, 3=result
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [parseError, setParseError] = useState('');
  const fileInputRef = useRef();

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setHeaders([]);
      setRows([]);
      setMapping({});
      setResult(null);
      setParseError('');
      fetchQuota();
    }
  }, [open]);

  async function fetchQuota() {
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.get(`${API_URL}/trades/import/quota`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setQuota(data);
    } catch {
      // non-critical
    }
  }

  function handleFile(file) {
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      setParseError('Please upload a .csv file');
      return;
    }
    setParseError('');

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          setParseError('CSV file appears to be empty or has no data rows');
          return;
        }
        const hdrs = results.meta.fields || [];
        if (hdrs.length === 0) {
          setParseError('Could not detect column headers in this CSV');
          return;
        }
        setHeaders(hdrs);
        setRows(results.data);
        setMapping(autoMapColumns(hdrs));
        setStep(2);
      },
      error: (err) => {
        setParseError(`Failed to parse CSV: ${err.message}`);
      },
    });
  }

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, []);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  async function handleImport() {
    const preview = rows.map(r => applyMapping(r, mapping));
    trackTradeImportStarted({ row_count: preview.length });
    setImporting(true);
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.post(`${API_URL}/trades/import`, {
        trades: preview,
        format: 'generic',
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      trackTradeImportCompleted({ imported_count: data.count ?? preview.length, skipped_count: preview.length - (data.count ?? preview.length) });
      setResult(data);
      setStep(3);
    } catch (err) {
      const d = err.response?.data;
      trackTradeImportFailed({ error_type: d?.error || 'unknown' });
      if (d?.error) {
        setResult({ error: d.error, limit: d.limit, used: d.used, resets: d.resets });
      } else {
        setResult({ error: 'Import failed. Please try again.' });
      }
      setStep(3);
    } finally {
      setImporting(false);
    }
  }

  const previewRows = rows.slice(0, 3).map(r => applyMapping(r, mapping));

  const canImport = REQUIRED_FIELDS.every(f => mapping[f.key]);

  const quotaInfo = () => {
    if (!quota) return null;
    if (quota.remaining === null) return null; // unlimited
    return `${quota.remaining} of ${quota.limit} imports remaining this month`;
  };

  const importCount = () => {
    if (!quota || quota.remaining === null) return rows.length;
    return Math.min(rows.length, quota.remaining);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Import Trades</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {step === 1 && 'Upload a CSV file from your broker'}
              {step === 2 && `${rows.length} rows detected — map columns to trade fields`}
              {step === 3 && 'Import complete'}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-border bg-muted/30">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${step === s ? 'text-primary' : step > s ? 'text-green-500' : 'text-muted-foreground'}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border ${
                  step > s ? 'bg-green-500 border-green-500 text-white' :
                  step === s ? 'border-primary text-primary' :
                  'border-border text-muted-foreground'
                }`}>
                  {step > s ? <Check className="w-3 h-3" /> : s}
                </div>
                {s === 1 ? 'Upload' : s === 2 ? 'Map Columns' : 'Done'}
              </div>
              {s < 3 && <div className="flex-1 h-px bg-border" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── Step 1: Upload ── */}
          {step === 1 && (
            <div className="space-y-4">
              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                  dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Drop your CSV file here</p>
                <p className="text-xs text-muted-foreground mt-1">or click to browse</p>
                <p className="text-xs text-muted-foreground mt-3">Supports MetaTrader 4/5, cTrader, OANDA, and any broker CSV</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>

              {parseError && (
                <div className="flex items-center gap-2 text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {parseError}
                </div>
              )}

              {quotaInfo() && (
                <p className="text-xs text-muted-foreground text-center">{quotaInfo()}</p>
              )}
            </div>
          )}

          {/* ── Step 2: Column Mapping ── */}
          {step === 2 && (
            <div className="space-y-5">
              {/* Mapping table */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-3">Map CSV columns to trade fields</h3>
                <div className="space-y-2">
                  {ALL_FIELDS.map((field) => (
                    <div key={field.key} className="grid grid-cols-2 gap-3 items-center">
                      <div>
                        <span className="text-sm text-foreground">{field.label}</span>
                        {REQUIRED_KEYS.has(field.key) && (
                          <span className="text-red-500 text-xs ml-1">*</span>
                        )}
                        <p className="text-xs text-muted-foreground">{field.description}</p>
                      </div>
                      <Select
                        value={(mapping[field.key] && mapping[field.key].trim()) ? mapping[field.key] : '__none__'}
                        onValueChange={(val) =>
                          setMapping(prev => ({ ...prev, [field.key]: val === '__none__' ? '' : val }))
                        }
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="— skip —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— skip —</SelectItem>
                          {headers.filter(h => h && h.trim()).map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">* required fields</p>
              </div>

              {/* Preview */}
              {previewRows.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-2">Preview (first {previewRows.length} rows)</h3>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-muted/50">
                          {['Symbol', 'Direction', 'Open Time', 'Entry', 'Close Time', 'Exit', 'Volume', 'Profit'].map(h => (
                            <th key={h} className="px-2 py-1.5 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((r, i) => (
                          <tr key={i} className="border-t border-border">
                            <td className="px-2 py-1.5 font-mono">{r.symbol || '—'}</td>
                            <td className={`px-2 py-1.5 font-medium ${r.direction === 'BUY' ? 'text-green-500' : r.direction === 'SELL' ? 'text-red-500' : 'text-muted-foreground'}`}>
                              {r.direction || '—'}
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{r.openTime ? new Date(r.openTime).toLocaleDateString() : '—'}</td>
                            <td className="px-2 py-1.5">{!isNaN(r.entry_price) ? r.entry_price : '—'}</td>
                            <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{r.closeTime ? new Date(r.closeTime).toLocaleDateString() : '—'}</td>
                            <td className="px-2 py-1.5">{r.exit_price != null && !isNaN(r.exit_price) ? r.exit_price : '—'}</td>
                            <td className="px-2 py-1.5">{r.lot_size || '—'}</td>
                            <td className={`px-2 py-1.5 font-medium ${parseFloat(r.profit) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {!isNaN(r.profit) ? r.profit : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Quota notice */}
              {quotaInfo() && (
                <div className={`text-xs rounded-lg px-3 py-2 ${
                  quota.remaining === 0
                    ? 'bg-red-500/10 text-red-400'
                    : rows.length > quota.remaining
                    ? 'bg-yellow-500/10 text-yellow-400'
                    : 'bg-muted/50 text-muted-foreground'
                }`}>
                  {quota.remaining === 0
                    ? `Monthly limit reached (${quota.limit}/month). Upgrade to import more.`
                    : rows.length > quota.remaining
                    ? `Only ${quota.remaining} of ${rows.length} rows will be imported — monthly limit.`
                    : quotaInfo()
                  }
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Result ── */}
          {step === 3 && result && (
            <div className="space-y-4">
              {result.error ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                  </div>
                  <p className="text-sm font-medium text-foreground">Import Failed</p>
                  <p className="text-xs text-muted-foreground mt-1">{result.error}</p>
                  {result.resets && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Resets on {new Date(result.resets).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <p className="text-sm font-semibold text-foreground mb-4">Import Complete</p>
                  <div className="inline-flex flex-col gap-2 text-left bg-muted/40 rounded-lg px-6 py-4 min-w-[200px]">
                    <div className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-foreground">Imported: <strong>{result.imported}</strong> trades</span>
                    </div>
                    {result.skipped > 0 && (
                      <div className="flex items-center gap-3">
                        <SkipForward className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Skipped: <strong>{result.skipped}</strong> duplicates</span>
                      </div>
                    )}
                    {result.truncated > 0 && (
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm text-yellow-600">Truncated: <strong>{result.truncated}</strong> (monthly limit)</span>
                      </div>
                    )}
                    {result.errors?.length > 0 && (
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-sm text-red-500">Errors: <strong>{result.errors.length}</strong> rows skipped</span>
                      </div>
                    )}
                  </div>

                  {result.errors?.length > 0 && (
                    <details className="mt-3 text-left">
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                        Show row errors
                      </summary>
                      <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                        {result.errors.map((e, i) => (
                          <p key={i} className="text-xs text-red-400">Row {e.row} ({e.symbol}): {e.issues.join(', ')}</p>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div>
            {step === 2 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 3 ? (
              <>
                <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
                {result && !result.error && result.imported > 0 && (
                  <Button size="sm" onClick={onImportComplete}>
                    View Trades
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </>
            ) : step === 2 ? (
              <Button
                size="sm"
                onClick={handleImport}
                disabled={!canImport || importing || (quota && quota.remaining === 0)}
              >
                {importing ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Importing…
                  </>
                ) : (
                  <>
                    Import {importCount()} trades
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
